import React, { useState, memo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';

const ImageSelectorDialog = memo(({
  open,
  onClose,
  images = [],
  loading,
  onSelect,
  onUploadComplete,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const schoolId = localStorage.getItem('schoolId');

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      const timestamp = Date.now();
      const autoTitle = `web_image_${schoolId}_${timestamp}`;
      const imageUrl = await axiosInstance.uploadImage(
        file,
        'IMAGES',
        schoolId,
        autoTitle,
        'Web Assets'
      );
      onSelect(imageUrl);
      onUploadComplete && onUploadComplete();
      setFile(null);
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 }}>
        Select or Upload Image
      </DialogTitle>
      <DialogContent sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.dark' }}>
          Existing Images
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
          </Box>
        ) : images?.length ? (
          <Box sx={{ height: 200, overflowY: 'auto' }}>
            <ImageList cols={4} gap={8}>
              {images.map((img) => (
                <ImageListItem
                  key={img.id}
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => { onSelect(img.fileUrl); onClose(); }}
                >
                  <img
                    src={img.fileUrl}
                    alt={img.eventName}
                    style={{ borderRadius: 4, width: '100%', height: 'auto', objectFit: 'cover' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            (No images found - upload below)
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.dark' }}>
            Upload New Image
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" size="small" component="label" sx={{ borderRadius: '6px' }}>
              Choose File
              <input hidden type="file" accept="image/*" onChange={handleFileChange} />
            </Button>
            {file && (
              <Typography variant="body2" sx={{ bgcolor: '#eee', p: 0.5, borderRadius: 1, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={!file || uploading}
              onClick={handleUpload}
              sx={{ borderRadius: '6px' }}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ borderRadius: '6px' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ImageSelectorDialog;
